import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAppStore } from '../../app-store';
import { LearningLegend } from '../../components/learning-legend/LearningLegend';
import { DRILLS } from '../../drills/registry';
import { SKILLS, type CardRecord, type Goal, type SkillStatus } from '../../engine';
import type { StatusOverlay } from '../../engine/overlay';
import { latestOutcomes } from '../../engine/progress';
import { startWithSoundSkills } from '../../engine/start-with-sound';
import { GOAL_OPTIONS } from '../../onboarding/options';
import { testOutGroups, unmetPrerequisites } from '../../placement/test-out';
import { createDrillPicker } from '../../session/drill-picker';
import { ScreenLayout } from '../../shell/ScreenLayout';
import { useSizeClass } from '../../shell/size-class';
import { allCards, getDb, getSkillStates } from '../../storage';
import { useStatusOverlay } from '../settings/DeveloperTools';
import { MasteryRing } from './MasteryRing';
import { ModuleDetail } from './ModuleDetail';
import {
  MODULE_STATE_LABEL,
  buildMap,
  defaultModule,
  percent,
  skillStatuses,
  type ModuleView,
} from './map-model';
import styles from './Learn.module.css';

const PICKER = createDrillPicker(SKILLS, DRILLS);
// The developer switch (M1-36) exists in development builds only; production drops it.
const useOverlay: () => StatusOverlay | null = import.meta.env.DEV ? useStatusOverlay : () => null;

interface LearnData {
  stored: Map<string, SkillStatus>;
  cards: CardRecord[];
  outcomes: Map<string, boolean>;
}

/** `version`: bump it to load again, e.g. after a test-out. */
function useLearnData(version = 0): LearnData | null {
  const [data, setData] = useState<LearnData | null>(null);
  useEffect(() => {
    let live = true;
    const db = getDb();
    void Promise.all([getSkillStates(db), allCards(db), db.reviewLogs.toArray()]).then(
      ([states, cards, logs]) => {
        if (!live) return;
        setData({
          stored: new Map([...states.values()].map((s) => [s.skillId, s.status])),
          cards,
          outcomes: latestOutcomes(logs),
        });
      },
    );
    return () => {
      live = false;
    };
  }, [version]);
  return data;
}

function ModuleCard({
  view,
  selected,
  replace,
}: {
  view: ModuleView;
  selected: boolean;
  replace: boolean;
}) {
  const { module } = view;
  const skills = view.skills.length;
  return (
    <li>
      <Link
        to={{ search: `?module=${module.id}` }}
        replace={replace}
        className={styles.moduleCard}
        data-state={view.state}
        data-testid={`module-${module.id}`}
        aria-current={selected ? 'true' : undefined}
      >
        <MasteryRing
          learning={view.learning}
          stable={view.mastery}
          state={view.state}
          label={module.title}
        />
        <span className={styles.moduleText}>
          <span className={styles.moduleTitle}>{module.title}</span>
          <span className={styles.moduleMeta}>
            {MODULE_STATE_LABEL[view.state]}
            {skills > 0 &&
              ` · ${view.masteredSkills}/${skills} skills mastered · ${percent(view.learning)}% learning · ${percent(view.mastery)}% stable`}
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * The first locked module, in the map's (goal) order, that has a test-out to
 * offer: a first skill locked by a prerequisite a drill can test. The same
 * check `TestOut` makes before it offers one in the module's detail.
 */
export function firstTestOut(
  map: readonly ModuleView[],
  statuses: ReadonlyMap<string, SkillStatus>,
): ModuleView | undefined {
  return map.find(
    (view) =>
      view.state === 'locked' &&
      view.skills.length > 0 &&
      testOutGroups(unmetPrerequisites(view.module, SKILLS, statuses), SKILLS, PICKER.canServe)
        .length > 0,
  );
}

/** Scroll to and focus the test-out offer in `pane`; false if it isn't there. */
function focusOffer(pane: HTMLElement | null): boolean {
  const offer = pane?.querySelector<HTMLElement>('[data-testid="test-out"]');
  if (!offer) return false;
  offer.scrollIntoView({ block: 'nearest' });
  offer.querySelector<HTMLButtonElement>('button')?.focus();
  return true;
}

/**
 * Bring a module's test-out offer into view and focus it, once that module's
 * detail shows in `pane`. Returns the setter: call it with the module ID as
 * the header opens the module. Moving to another module drops the jump, so a
 * later visit never has its focus taken.
 */
export function useOfferJump(
  selectedId: string | undefined,
  pane: RefObject<HTMLElement | null>,
): (moduleId: string) => void {
  // Refs, not state: only a change of module needs to run the jump.
  const jumpTo = useRef<string | null>(null);
  const shown = useRef(selectedId);
  useEffect(() => {
    shown.current = selectedId;
    if (jumpTo.current && (selectedId !== jumpTo.current || focusOffer(pane.current)))
      jumpTo.current = null;
  }, [selectedId, pane]);
  return useCallback(
    (moduleId: string) => {
      jumpTo.current = moduleId;
      // Already showing: there's no change of module to wait for.
      if (shown.current === moduleId && focusOffer(pane.current)) jumpTo.current = null;
    },
    [pane],
  );
}

/**
 * Learn (design §7): the module map with a mastery ring per module, ordered
 * by the learner's goal. Two panes on iPad — the map on the left, the picked
 * module's skill detail on the right; on compact the detail replaces the map.
 * Locked modules open like any other (soft unlocking, §3.4). The map's
 * header offers a test-out of the first locked module (M10-16): it opens that
 * module with its "Already know this?" offer in view and focused.
 */
export function LearnScreen() {
  const goal = useAppStore((s) => s.settings.goal);
  const startWithSound = useAppStore((s) => s.settings.startWithSound);
  // Gated as the session runner gates them ("start with sound" opens the ear track).
  const skills = useMemo(() => startWithSoundSkills(SKILLS, startWithSound), [startWithSound]);
  const update = useAppStore((s) => s.update);
  const loaded = useAppStore((s) => s.loaded);
  const sizeClass = useSizeClass();
  const compact = sizeClass === 'compact';
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const data = useLearnData(version);
  const statuses = useMemo(() => data && skillStatuses(skills, data.stored), [skills, data]);
  const overlay = useOverlay();
  const map = useMemo(
    () =>
      buildMap({
        skills,
        goal,
        stored: data?.stored ?? new Map(),
        cards: data?.cards ?? [],
        outcomes: data?.outcomes,
        canServe: PICKER.canServe,
        overlay,
      }),
    [skills, goal, data, overlay],
  );
  const picked = map.find((m) => m.module.id === params.get('module'));
  const selected = picked ?? (compact ? undefined : defaultModule(map));
  const showMap = !compact || !selected;
  const testOut = useMemo(
    () => (loaded && statuses ? firstTestOut(map, statuses) : undefined),
    [loaded, map, statuses],
  );
  const detailPane = useRef<HTMLDivElement>(null);
  const jumpToOffer = useOfferJump(selected?.module.id, detailPane);

  return (
    <ScreenLayout title="Learn">
      <div className={styles.learn} data-size-class={sizeClass} data-loaded={data !== null}>
        {showMap && (
          <section className={styles.map} aria-label="Modules">
            <label className={styles.goal}>
              <span>Ordered for your goal</span>
              <select
                value={goal}
                data-testid="goal-select"
                onChange={(e) => void update({ goal: e.target.value as Goal })}
              >
                {GOAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {testOut && (
              <p className={styles.testOutEntry} data-testid="learn-test-out">
                <span>Already play some of this?</span>
                {/* An action, like the offer's own button: it opens the module and its test-out. */}
                <button
                  type="button"
                  className={styles.testOutButton}
                  onClick={() => {
                    jumpToOffer(testOut.module.id);
                    void navigate(
                      { search: `?module=${testOut.module.id}` },
                      { replace: !compact },
                    );
                  }}
                >
                  Test out of {testOut.module.title}
                </button>
              </p>
            )}
            {/* Defined where the numbers first appear (copy audit, screens §9). */}
            <LearningLegend className={styles.legend} />
            <ul className={styles.modules}>
              {map.map((view) => (
                <ModuleCard
                  key={view.module.id}
                  view={view}
                  selected={view === selected}
                  replace={!compact}
                />
              ))}
            </ul>
          </section>
        )}
        {selected && (
          <div className={styles.detailPane} ref={detailPane}>
            {compact && (
              <Link to="/learn" replace className={styles.back}>
                ← All modules
              </Link>
            )}
            <ModuleDetail
              view={selected}
              statuses={statuses ?? undefined}
              onChange={() => setVersion((v) => v + 1)}
            />
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}
